import { Component, OnInit, OnDestroy, AfterViewInit, AfterViewChecked, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { of, Subscription } from 'rxjs';
import { switchMap, catchError, skip } from 'rxjs/operators';
import { DealerPortalService } from 'src/app/services/dealerPortal.service';
import { EcatService } from 'src/app/services/ecat.service';
import { CartService } from 'src/app/services/cart.service';
import { CustomerService } from 'src/app/services/customer.service';
import { SharedDataService } from 'src/app/services/sharedDataService.service';
import { environment } from 'src/environments/environment';
import { NzMessageService } from 'ng-zorro-antd/message';

interface PromotionProduct {
  status: string;
  statusType: 'warning' | 'info' | 'hot' | 'new' | 'primary';
  category: string;
  title: string;
  materialNo: string;
  articleNumber: string;
  partId: string;
  minOrderQty: number;
  quantity: number;
  description: string;
  ctaText: string;
  imageUrl: string;
}

interface PromotionActivitySummary {
  activityId: number;
  activityName: string;
  /** 活动状态：0=即将开始，1=进行中，2=已结束 */
  activityStatus?: number;
  /** 奖券图片文件 ID（后台 750*400 裁切上传），由 activity-summary 接口返回 */
  awardImgUrl?: string;
  /** 奖品业务名称，如“积分”“抵用券”，已结束卡片奖励文案用 */
  awardBizName?: string;
  activityContents: string;
  startTime: string;
  endTime: string;
  baselineType: string;
  activityMechanism: {
    type: string;
    value: number;
    award: {
      bizType: string;
      bizId: string;
      bizName: string;
      sendQty: number;
      imgUrl?: string;
    };
  };
  awardDistributionTimeliness: string;
  disclaimer: string;
  issuedAwardQty: number | null;
  redeemedAwardQty: number | null;
  progressCurrent: number;
  progressTarget: number;
  progressRemaining: number;
}

interface RedemptionDetail {
  voucherCode: string;
  orderCode: string;
  orderStatus: string;
  logisticsCode: string;
}

/** /promotion/front/activity/{activityId}/redeem-records 返回的卡券核销记录 */
interface PromotionCouponDetail {
  couponInstanceId?: string;
  couponCode?: string;
  couponName?: string;
  couponStatus?: string;
  orderNumber?: string;
  orderStatus?: string;
  logisticsNumber?: string;
}

interface EndedActivity {
  activityId: number;
  activityName: string;
  startTime: string;
  endTime: string;
  activityContents: string;
  earnedAwardLabel: string;   // 如 "1,200 积分" / "500元 抵用券"
  redeemedQty: number;
  canRedeem: boolean;         // 控制“去兑换”/“查看详情”是否可点
  canViewRedemption: boolean; // 控制“核销详情”是否可点
}

/** /promotion/front/history/activity-summary 返回体 */
interface PromotionHistoryActivitySummary {
  /** 已结束活动累计获得卡券总数 */
  totalEarnedAwardQty: number;
  /** 已结束活动累计已兑换卡券总数 */
  totalRedeemedAwardQty: number;
  /** 已结束活动摘要分页列表（rows 字段与当前活动汇总一致） */
  activityList: {
    /** 满足条件的总记录数 */
    total: number;
    /** 当前页数据 */
    rows: PromotionActivitySummary[];
  };
}

@Component({
  selector: 'app-promotion-activity',
  templateUrl: './promotion-activity.component.html',
  styleUrls: ['./promotion-activity.component.less']
})
export class PromotionActivityComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  activityList: PromotionActivitySummary[] = [];
  activeSlideIndex = 0;
  private autoPlayTimer: any = null;
  isDealerPortal: boolean = false;

  heroLoading = true;
  productsLoading = false;
  loadingMore = false;

  /** 当前是否处于“按料号搜索”模式（非已结束 tab 且搜索框有内容），用于区分加载/空状态文案 */
  get isSearchingByPartNo(): boolean {
    return this.statusFilter !== 'ended' && !!this.searchKeyword?.trim();
  }

  searchKeyword = '';

  tabs: string[] = ['全部'];
  selectedTab = '全部';
  statusFilter = 'ongoing';
  category1NameList: string[] = [];

  products: PromotionProduct[] = [];
  joinedActivityId: number | null = null;
  redeemModalVisible = false;
  processPreviewVisible = false;
  redemptionModalVisible = false;
  redemptionDetails: RedemptionDetail[] = [];
  redemptionLoading = false;
  disclaimerModalVisible = false;
  currentDisclaimer = '';
  /** 奖券图加载失败的活动 ID 集合，模板据此回退为图标占位（180*180 方图铺在 750*400 横版槽内不美观，不再使用） */
  awardImgBroken = new Set<number>();

  /** 兑换流程放大弹窗：图片缩放比例与拖拽平移偏移 */
  previewZoom = 1;
  previewOffsetX = 0;
  previewOffsetY = 0;
  readonly PREVIEW_ZOOM_MIN = 1;
  readonly PREVIEW_ZOOM_MAX = 4;
  readonly PREVIEW_ZOOM_STEP = 0.25;
  private isDraggingPreview = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartOffsetX = 0;
  private dragStartOffsetY = 0;

  /** 放大弹窗图片的内联样式：缩放 + 平移，并按状态切换光标 */
  get previewImageStyle(): { [key: string]: string } {
    return {
      transform: `translate(${this.previewOffsetX}px, ${this.previewOffsetY}px) scale(${this.previewZoom})`,
      cursor: this.previewZoom > 1 ? (this.isDraggingPreview ? 'grabbing' : 'grab') : 'zoom-in',
    };
  }

  /** 打开兑换流程放大弹窗，并重置上次的缩放/平移状态 */
  openProcessPreview(): void {
    this.resetPreviewTransform();
    this.processPreviewVisible = true;
  }

  /** 关闭兑换流程放大弹窗 */
  closeProcessPreview(): void {
    this.processPreviewVisible = false;
  }

  resetPreviewTransform(): void {
    this.previewZoom = 1;
    this.previewOffsetX = 0;
    this.previewOffsetY = 0;
    this.isDraggingPreview = false;
  }

  previewZoomIn(): void {
    this.previewZoom = Math.min(this.PREVIEW_ZOOM_MAX, +(this.previewZoom + this.PREVIEW_ZOOM_STEP).toFixed(2));
    if (this.previewZoom === 1) {
      this.previewOffsetX = 0;
      this.previewOffsetY = 0;
    }
  }

  previewZoomOut(): void {
    this.previewZoom = Math.max(this.PREVIEW_ZOOM_MIN, +(this.previewZoom - this.PREVIEW_ZOOM_STEP).toFixed(2));
    if (this.previewZoom === 1) {
      this.previewOffsetX = 0;
      this.previewOffsetY = 0;
    }
  }

  /** 滚轮缩放（向上放大、向下缩小） */
  onPreviewWheel(e: WheelEvent): void {
    e.preventDefault();
    if (e.deltaY < 0) {
      this.previewZoomIn();
    } else {
      this.previewZoomOut();
    }
  }

  onPreviewDragStart(e: MouseEvent): void {
    if (this.previewZoom <= 1) {
      return;
    }
    this.isDraggingPreview = true;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartOffsetX = this.previewOffsetX;
    this.dragStartOffsetY = this.previewOffsetY;
    e.preventDefault();
  }

  onPreviewDragMove(e: MouseEvent): void {
    if (!this.isDraggingPreview) {
      return;
    }
    this.previewOffsetX = this.dragStartOffsetX + (e.clientX - this.dragStartX);
    this.previewOffsetY = this.dragStartOffsetY + (e.clientY - this.dragStartY);
  }

  onPreviewDragEnd(): void {
    this.isDraggingPreview = false;
  }

  /** 已结束活动：数据来自 /promotion/front/history/activity-summary */
  endedSummary = {
    totalEarned: 0,
    totalRedeemed: 0,
  };
  endedActivities: EndedActivity[] = [];
  endedLoading = false;
  /** 已结束列表分页：首页大小固定 6 条，向下滚动加载更多 */
  readonly ENDED_PAGE_SIZE = 6;
  endedHasMore = false;
  endedLoadingMore = false;
  private endedCurrentPage = 1;
  private endedTotalCount = 0;

  openDisclaimer(activity: PromotionActivitySummary): void {
    this.currentDisclaimer = activity?.disclaimer ?? '';
    this.disclaimerModalVisible = true;
  }

  /** 分页相关 */
  private allPartNos: string[] = [];
  private currentPage = 1;
  readonly PAGE_SIZE = 6;
  /** 第二行活动筛选 tab 最多直接展示的数量，超出则收起为 “···” 并引导至右侧“所有活动”下拉框 */
  readonly MAX_VISIBLE_TABS = 6;
  hasMore = false;
  private totalCount = 0;

  @ViewChild('scrollSentinel') scrollSentinelRef!: ElementRef;
  @ViewChild('activityFilterBar') activityFilterBarRef?: ElementRef;
  private observer: IntersectionObserver | null = null;
  /** 当前已注册到 observer 上的哨兵节点，用于判断 *ngIf 重建后是否需要重新监听 */
  private observedSentinel: Element | null = null;
  private customerSubscription: Subscription | undefined;

  constructor(
    private dealerPortalService: DealerPortalService,
    private ecatService: EcatService,
    private cartService: CartService,
    private customerService: CustomerService,
    private message: NzMessageService,
    private router: Router,
    private sharedData: SharedDataService,
  ) {
    this.sharedData.currentData.subscribe(data => {
      this.isDealerPortal = data.isDealerPortal;
    });
  }

  ngOnInit(): void {
    this.loadActivitySummary();
    // 切换客户时重置页面状态并重新加载数据（skip(1) 跳过 BehaviorSubject 的初始值）
    this.customerSubscription = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => {
        this.handleCustomerChange();
      });
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        // 同一 #scrollSentinel 引用在产品列表与已结束列表两处 *ngIf 中复用，根据当前 tab 分发
        if (this.statusFilter === 'ended') {
          this.loadMoreEndedActivities();
        } else {
          this.loadMoreProducts();
        }
      }
    }, { rootMargin: '100px' });
    this.syncSentinelObserver();
  }

  /**
   * 哨兵元素由 *ngIf 控制（已结束 tab 不展示），节点会被销毁重建，
   * 旧节点上的监听会失效，因此每次视图校验后检查并重新挂载新节点
   */
  ngAfterViewChecked(): void {
    this.syncSentinelObserver();
  }

  /** ref 指向的节点发生变化时重新注册 IntersectionObserver */
  private syncSentinelObserver(): void {
    const el = this.scrollSentinelRef?.nativeElement ?? null;
    if (!this.observer || el === this.observedSentinel) { return; }
    this.observer.disconnect();
    if (el) { this.observer.observe(el); }
    this.observedSentinel = el;
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
    this.observer?.disconnect();
    this.observedSentinel = null;
    this.customerSubscription?.unsubscribe();
  }

  /** 切换客户：重置筛选/活动状态并重新加载活动与产品数据 */
  private handleCustomerChange(): void {
    this.activeSlideIndex = 0;
    this.stopAutoPlay();
    this.joinedActivityId = null;
    this.searchKeyword = '';
    this.selectedTab = '全部';
    this.statusFilter = 'ongoing';
    this.category1NameList = [];
    // 历史活动也是客户维度数据，切客户后需重新拉取（此时已回到“进行中”tab，下次切入时再请求）
    this.resetEndedSection();
    this.loadActivitySummary();
  }

  /** 清空已结束 tab 的汇总、列表与分页状态 */
  private resetEndedSection(): void {
    this.endedSummary = { totalEarned: 0, totalRedeemed: 0 };
    this.endedActivities = [];
    this.endedHasMore = false;
    this.endedLoadingMore = false;
    this.endedCurrentPage = 1;
    this.endedTotalCount = 0;
  }

  private loadActivitySummary(): void {
    this.heroLoading = true;
    this.dealerPortalService.getPromotionActivitySummary({ type: 'banner' }).subscribe({
      next: (res) => {
        const list: PromotionActivitySummary[] = res?.rtnData ?? [];
        this.activityList = list;
        this.awardImgBroken.clear();
        if (list.length > 1) { this.startAutoPlay(); }
        this.heroLoading = false;
        this.loadProducts();
      },
      error: () => {
        this.heroLoading = false;
      },
    });
  }

  get currentActivity(): PromotionActivitySummary | null {
    return this.activityList[this.activeSlideIndex] ?? null;
  }

  /**
   * 查询已结束活动汇总（当前客户维度）首页，搜索框关键词作为 activityName 模糊查询参数；
   * 返回体为汇总对象（totalEarnedAwardQty / totalRedeemedAwardQty / activityList{total,rows}）
   */
  private loadEndedActivities(): void {
    this.endedLoading = true;
    this.endedCurrentPage = 1;
    this.endedActivities = [];
    this.endedHasMore = false;
    this.endedLoadingMore = false;
    this.dealerPortalService
      .getPromotionHistoryActivitySummary(this.endedCurrentPage, this.ENDED_PAGE_SIZE, this.searchKeyword?.trim())
      .subscribe({
        next: (res) => {
          // 非成功响应（如 token 失效）的 rtnData 可能为空壳形态，统一兜底为空列表
          const data = (res?.rtnCode === 10000 ? res.rtnData : null) as PromotionHistoryActivitySummary | null;
          const page = data?.activityList;
          const rows: PromotionActivitySummary[] = page && Array.isArray(page.rows) ? page.rows : [];
          this.endedSummary = {
            totalEarned: data?.totalEarnedAwardQty ?? 0,
            totalRedeemed: data?.totalRedeemedAwardQty ?? 0,
          };
          this.endedActivities = rows.filter((item) => !!item).map((item) => this.mapEndedActivity(item));
          this.endedTotalCount = page?.total ?? this.endedActivities.length;
          this.endedHasMore = this.endedActivities.length < this.endedTotalCount;
          this.endedCurrentPage = 2;
          this.endedLoading = false;
        },
        error: () => {
          this.resetEndedSection();
          this.endedLoading = false;
        },
      });
  }

  /** 已结束列表滚动到底部时加载下一页（累加，不覆盖汇总数据） */
  loadMoreEndedActivities(): void {
    if (this.statusFilter !== 'ended' || !this.endedHasMore || this.endedLoadingMore || this.endedLoading) {
      return;
    }
    this.endedLoadingMore = true;
    this.dealerPortalService
      .getPromotionHistoryActivitySummary(this.endedCurrentPage, this.ENDED_PAGE_SIZE, this.searchKeyword?.trim())
      .subscribe({
        next: (res) => {
          const data = (res?.rtnCode === 10000 ? res.rtnData : null) as PromotionHistoryActivitySummary | null;
          const rows: PromotionActivitySummary[] = data?.activityList?.rows ?? [];
          const newItems = rows.filter((item) => !!item).map((item) => this.mapEndedActivity(item));
          this.endedActivities = [...this.endedActivities, ...newItems];
          this.endedHasMore = this.endedActivities.length < this.endedTotalCount;
          this.endedCurrentPage++;
          this.endedLoadingMore = false;
        },
        error: () => {
          this.endedLoadingMore = false;
        },
      });
  }

  /** 后端活动摘要 → 已结束卡片视图模型 */
  private mapEndedActivity(item: PromotionActivitySummary): EndedActivity {
    const earnedQty = item.issuedAwardQty ?? 0;
    const redeemedQty = item.redeemedAwardQty ?? 0;
    return {
      activityId: item.activityId,
      activityName: item.activityName ?? '',
      startTime: item.startTime,
      endTime: item.endTime,
      activityContents: item.activityContents ?? '',
      // 已获奖励展示“数量 + 奖品名称”，如 "1,200 积分"
      earnedAwardLabel: `${earnedQty.toLocaleString('en-US')}张 ${item.awardBizName ?? ''}`.trim(),
      redeemedQty,
      // 无卡券时不可去兑换；无兑换记录时核销详情置灰
      canRedeem: earnedQty > 0,
      canViewRedemption: redeemedQty > 0,
    };
  }

  get pageTitle(): string { return this.currentActivity?.activityName || ''; }
  get pageSubtitle(): string { return this.currentActivity?.activityContents || ''; }
  get earnedVoucherCount(): number { return this.currentActivity?.issuedAwardQty ?? 0; }
  get redeemedVoucherCount(): number { return this.currentActivity?.redeemedAwardQty ?? 0; }
  get nextVoucherProgress(): number { return this.currentActivity?.progressCurrent ?? 0; }
  get nextVoucherTarget(): number { return this.currentActivity?.progressTarget ?? 0; }
  get progressRemaining(): number { return this.currentActivity?.progressRemaining ?? 0; }
  get activityTimeRange(): string {
    const a = this.currentActivity;
    return a ? this.formatTimeRange(a.startTime, a.endTime) : '';
  }

  goToSlide(index: number): void {
    this.activeSlideIndex = index;
    this.resetAutoPlay();
  }

  /** 状态 tab → activityStatus 码（0=即将开始,1=进行中,2=已结束） */
  private statusToActivityCode(status: string): number | null {
    switch (status) {
      case 'upcoming': return 0;
      case 'ongoing': return 1;
      case 'ended': return 2;
      default: return null;
    }
  }

  /** activityStatus 码 → 状态 tab */
  private activityCodeToStatus(code?: number): 'ongoing' | 'upcoming' | 'ended' {
    switch (code) {
      case 0: return 'upcoming';
      case 2: return 'ended';
      default: return 'ongoing';
    }
  }

  /** 后端是否返回了 activityStatus 字段 */
  private get hasActivityStatusField(): boolean {
    return this.activityList.some(a => a.activityStatus !== undefined && a.activityStatus !== null);
  }

  /** 当前状态 tab 下需要展示的活动（第二行 filter bar 的数据源） */
  get activitiesForCurrentStatus(): PromotionActivitySummary[] {
    const code = this.statusToActivityCode(this.statusFilter);
    if (code === null) { return []; }
    if (!this.hasActivityStatusField) {
      // 兜底：后端未返回 activityStatus 时，“进行中/即将上线”回退展示全部活动；“已结束”走独立的历史活动摘要接口，这里返回空
      return code === 2 ? [] : this.activityList;
    }
    return this.activityList.filter(a => a.activityStatus === code);
  }

  /** 活动数是否超过可见 tab 上限，用于在右侧展示 “···” 溢出标识 */
  get hasMoreActivityTabs(): boolean {
    return this.activitiesForCurrentStatus.length > this.MAX_VISIBLE_TABS;
  }

  /**
   * 第二行实际渲染的活动 tab（最多 MAX_VISIBLE_TABS 个）。
   * 若从“所有活动”下拉框选中的活动不在前 6 个内（如第 7 个），则将其提升到最左侧展示并保持选中。
   * 配 trackByActivityId 使用，避免 getter 新数组导致 *ngFor 重建 DOM。
   */
  get visibleActivityTabs(): PromotionActivitySummary[] {
    const all = this.activitiesForCurrentStatus;
    if (all.length <= this.MAX_VISIBLE_TABS) { return all; }
    const firstN = all.slice(0, this.MAX_VISIBLE_TABS);
    const selected = all.find(a => a.activityId === this.joinedActivityId);
    if (selected && !firstN.includes(selected)) {
      // 将选中活动置顶，后接前 5 个，总共仍不超过 MAX_VISIBLE_TABS
      return [selected, ...firstN.slice(0, this.MAX_VISIBLE_TABS - 1)];
    }
    return firstN;
  }

  /** 点击第二行活动名称：选中/取消该活动并展示对应产品 */
  onSelectActivityFilter(activity: PromotionActivitySummary): void {
    if (!activity?.activityId) { return; }
    if (this.joinedActivityId === activity.activityId) {
      // 再次点击已选中的活动：取消筛选，回到当前状态下的全部产品
      this.onClearActivity();
      return;
    }
    this.joinedActivityId = activity.activityId;
    this.searchKeyword = '';
    this.selectedTab = '全部';
    this.loadProducts();
  }

  /** 将选中的活动 tab 横向滚动到可见区域 */
  private scrollActiveChipIntoView(): void {
    setTimeout(() => {
      const bar = this.activityFilterBarRef?.nativeElement;
      if (!bar) { return; }
      const active = bar.querySelector('.activity-tab.active') as HTMLElement | null;
      active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  }

  /** activitiesForCurrentStatus 为 getter，配 trackBy 避免每次变更检测重建 chip DOM */
  trackByActivityId(_: number, activity: PromotionActivitySummary): number {
    return activity.activityId;
  }

  prevSlide(): void {
    this.activeSlideIndex = (this.activeSlideIndex - 1 + this.activityList.length) % this.activityList.length;
    this.resetAutoPlay();
  }

  nextSlide(): void {
    this.activeSlideIndex = (this.activeSlideIndex + 1) % this.activityList.length;
    this.resetAutoPlay();
  }

  private startAutoPlay(): void {
    this.autoPlayTimer = setInterval(() => {
      this.activeSlideIndex = (this.activeSlideIndex + 1) % this.activityList.length;
    }, 10000);
  }

  private stopAutoPlay(): void {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  private resetAutoPlay(): void {
    this.stopAutoPlay();
    if (this.activityList.length > 1) { this.startAutoPlay(); }
  }

  get remainingAmount(): number {
    return this.progressRemaining > 0
      ? this.progressRemaining
      : Math.max(0, this.nextVoucherTarget - this.nextVoucherProgress);
  }

  get progressRate(): number {
    if (!this.nextVoucherTarget) { return 0; }
    return Math.round((this.nextVoucherProgress / this.nextVoucherTarget) * 100);
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
  }

  onSearch(): void {
    // 已结束 tab 的搜索框按活动名称模糊查询历史活动摘要，不走产品搜索
    if (this.statusFilter === 'ended') {
      this.loadEndedActivities();
      return;
    }
    const keyword = this.searchKeyword?.trim();
    if (!keyword) {
      // 输入框为空：按活动接口返回的 allPartNos 查询默认产品列表
      this.loadProducts();
      return;
    }
    // 搜索模式下禁用滚动加载更多：清空 allPartNos、关闭 hasMore，避免误触发分页查询
    this.allPartNos = [];
    this.hasMore = false;
    this.currentPage = 1;
    this.products = [];
    this.productsLoading = true;
    // 当前选中的活动范围：已参与活动只查该活动，否则查当前状态 tab 下的全部活动
    const activityIds = this.joinedActivityId
      ? [this.joinedActivityId]
      : this.activitiesForCurrentStatus.map(a => a.activityId).filter(Boolean);
    this.dealerPortalService.queryGlobalPartNos([keyword], activityIds).subscribe({
      next: (res) => {
        // 后端以标准 ResponseData 返回，料号列表在 rtnData 中（兼容 ecat 原始 resultData 形态）
        const data: any = res?.rtnData;
        const partList: any[] = data?.partList ?? data?.resultData?.partList ?? [];
        this.products = (partList ?? []).map((p: any) => this.mapPartToProduct(p));
        this.productsLoading = false;
      },
      error: () => {
        this.products = [];
        this.productsLoading = false;
      },
    });
  }

  /** 点击"立即参与"：标记当前活动，自动定位到对应状态的活动筛选行，并根据 activityId 重新查询产品 */
  onJoinActivity(activity: PromotionActivitySummary): void {
    if (!activity?.activityId) { return; }
    this.joinedActivityId = activity.activityId;
    // 自动定位：切换到该活动所属的状态 tab，使第二行筛选器高亮对应 activityName
    this.statusFilter = this.activityCodeToStatus(activity.activityStatus);
    this.searchKeyword = '';
    this.selectedTab = '全部';
    this.loadProducts();
    this.scrollActiveChipIntoView();
    if (environment.production) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      (window as any).dataLayer.push({
        event: 'dp_promotion_banner_view',
        from_page:
          (localStorage.getItem('token') ? 'afterLogin ' : 'beforeLogin ') +
          window.location.pathname,
        event_type: '',
        event_name: '',
        portal_name: this.isDealerPortal
          ? 'DealerPortal ' + customer?.customerCode
          : 'SalesPortal ' + userData?.queryUserPageInfoResDTO?.ntAccount,
        event_content: (this.isDealerPortal
          ? customer?.customerCode
          : userData?.queryUserPageInfoResDTO?.ntAccount) +
          ' ' + (activity?.activityId ?? ''),
      });
    }
  }

  /** 点击"进行中"：清除活动标记，重新加载全部产品 */
  onBackToAll(): void {
    this.joinedActivityId = null;
    this.searchKeyword = '';
    this.selectedTab = '全部';
    this.statusFilter = 'ongoing';
    this.loadProducts();
    if (environment.production) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      (window as any).dataLayer.push({
        event: 'dp_promotion_productlist',
        from_page:
          (localStorage.getItem('token') ? 'afterLogin ' : 'beforeLogin ') +
          window.location.pathname,
        event_type: '进行中',
        event_name: '',
        portal_name: this.isDealerPortal
          ? 'DealerPortal ' + customer?.customerCode
          : 'SalesPortal ' + userData?.queryUserPageInfoResDTO?.ntAccount,
        event_content: (this.isDealerPortal
          ? customer?.customerCode
          : userData?.queryUserPageInfoResDTO?.ntAccount),
      });
    }
  }

  /** 清除活动筛选：取消选中的活动，重新加载全部产品 */
  onClearActivity(): void {
    this.joinedActivityId = null;
    this.selectedTab = '全部';
    this.loadProducts();
  }

  /** 切换状态 tab（进行中 / 即将上线 / 已结束） */
  onStatusFilterChange(status: 'ongoing' | 'upcoming' | 'ended'): void {
    if (this.statusFilter === status && status === 'ongoing') {
      // 已在"进行中" tab 上再次点击，保持原有刷新行为
      this.onBackToAll();
      return;
    }
    this.statusFilter = status;
    this.joinedActivityId = null;
    this.searchKeyword = '';
    this.selectedTab = '全部';
    // 已结束 tab 的数据来自历史活动摘要接口，不调用产品列表接口
    if (status === 'ended') {
      this.loadingMore = false;
      this.loadEndedActivities();
    } else {
      this.loadProducts();
    }
  }

  openRedeemModal(): void {
    this.redeemModalVisible = true;
  }

  /**
   * 打开核销详情弹窗：按活动 ID 调 /promotion/front/activity/{activityId}/redeem-records，
   * 进行中轮播图与已结束卡片共用（只依赖 activityId）
   */
  openRedemptionDetail(activity: { activityId: number }): void {
    const activityId = activity?.activityId;
    if (!activityId) { return; }
    this.redemptionDetails = [];
    this.redemptionLoading = true;
    this.redemptionModalVisible = true;
    this.dealerPortalService.getPromotionRedeemRecords(activityId).subscribe({
      next: (res) => {
        // 非成功响应的 rtnData 可能为空壳形态，统一兜底为空列表
        const rows: PromotionCouponDetail[] =
          res?.rtnCode === 10000 && Array.isArray(res?.rtnData) ? res.rtnData : [];
        this.redemptionDetails = rows
          .filter((row) => !!row)
          .map((row) => ({
            voucherCode: row.couponCode ?? '',
            orderCode: row.orderNumber ?? '',
            orderStatus: row.orderStatus ?? '',
            logisticsCode: row.logisticsNumber ?? '',
          }));
        this.redemptionLoading = false;
      },
      error: () => {
        this.redemptionDetails = [];
        this.redemptionLoading = false;
      },
    });
  }

  /** 链式调用: queryActivityPartNos → getPartListByPartNos (首页) */
  private loadProducts(): void {
    this.productsLoading = true;
    this.products = [];
    this.currentPage = 1;
    this.hasMore = false;
    // 重新查询时取消“正在加载更多”展示，避免旧 tab 的加载态残留到新 tab
    this.loadingMore = false;
    // 根据当前状态决定查询范围：已参与活动只查该活动，否则查当前状态 tab 下（按 activityStatus 过滤）的活动
    const activityIdList = this.joinedActivityId
      ? [this.joinedActivityId]
      : this.activitiesForCurrentStatus.map(a => a.activityId).filter(Boolean);
    if (activityIdList.length === 0) {
      this.products = [];
      this.productsLoading = false;
      return;
    }
    this.dealerPortalService.queryActivityPartNos(activityIdList).pipe(
      switchMap((res) => {
        const items: any[] = res?.rtnData ?? [];
        // 只要存在某个活动的 partNoList 为空，则整体视为无可选产品（allPartNos 置空）；
        // 全部都有值时，才把各活动的 partNoList 拼接起来
        const partNoLists = items.map(item => item.partNoList ?? []);
        const hasEmptyPartNo = partNoLists.some(list => list.length === 0);
        this.allPartNos = hasEmptyPartNo
          ? []
          : partNoLists.reduce((acc: string[], list) => acc.concat(list), []);
        // partNo 列表为空时也继续查询（传空数组），由后端返回空结果
        return this.ecatService.getPartListByPartNos(this.allPartNos, this.currentPage, this.PAGE_SIZE);
      }),
      catchError(() => of(null)),
    ).subscribe({
      next: (res) => {
        if (res?.resultData) {
          const { category1NameList, partList, pageInfo } = res.resultData;
          this.category1NameList = category1NameList ?? [];
          this.tabs = ['全部', ...this.category1NameList];
          this.products = (partList ?? []).map((p: any) => this.mapPartToProduct(p));
          this.totalCount = pageInfo?.totalCounts ?? 0;
          this.hasMore = this.products.length < this.totalCount;
          this.currentPage = 2;
        } else {
          this.products = [];
          this.hasMore = false;
        }
        this.productsLoading = false;
      },
      error: () => {
        this.products = [];
        this.hasMore = false;
        this.productsLoading = false;
      },
    });
  }

  /** 滚动到底部时加载下一页产品 */
  loadMoreProducts(): void {
    // 已结束 tab 不触发产品加载；搜索模式不触发
    if (this.statusFilter === 'ended' || this.searchKeyword?.trim() || !this.hasMore || this.loadingMore || this.productsLoading) { return; }
    this.loadingMore = true;
    this.ecatService.getPartListByPartNos(this.allPartNos, this.currentPage, this.PAGE_SIZE).subscribe({
      next: (res) => {
        if (res?.resultData?.partList) {
          const newProducts = res.resultData.partList.map((p: any) => this.mapPartToProduct(p));
          this.products = [...this.products, ...newProducts];
          this.hasMore = this.products.length < this.totalCount;
          this.currentPage++;
        } else {
          this.hasMore = false;
        }
        this.loadingMore = false;
      },
      error: () => {
        this.loadingMore = false;
      },
    });
  }

  /** 将 ecat 配件数据映射为 PromotionProduct */
  private mapPartToProduct(p: any): PromotionProduct {
    const minOrderQty = p.minumQty ?? 1;
    return {
      status: (p.stockQty ?? 0) > 0 ? '促销中' : '暂无库存',
      statusType: (p.stockQty ?? 0) > 0 ? 'primary' : 'info',
      category: p.category1Name ?? '',
      title: p.partName ?? '',
      materialNo: p.partNo ?? '',
      articleNumber: p.articleNumber ?? p.partNo ?? '',
      partId: p.partId ?? '',
      minOrderQty,
      quantity: 1,
      description: p.partAlias || p.partDesc || '',
      ctaText: (p.stockQty ?? 0) > 0 ? '加入采购清单' : '到货提醒',
      imageUrl: this.buildImageUrl(p.imageUrl),
    };
  }

  /** 点击产品卡片跳转到产品详情页 */
  goDetail(product: PromotionProduct): void {
    const country = localStorage.getItem("country");
    const url = country ? `${country}/product/detail` : '/product/detail';
    this.router.navigate([url], { queryParams: { articleNumber: product.articleNumber, partId: product.partId } });
  }

  /** 加入购物车 */
  onAddToCart(product: PromotionProduct): void {
    if (!product.quantity || product.quantity <= 0) {
      this.message.warning('请输入有效的数量');
      return;
    }
    const today = moment().format('YYYY-MM-DD');
    this.cartService.addToCart([{
      expectedDate: today,
      quantity: product.quantity,
      inputMaterialNumber: product.materialNo,
    }]).then(() => {
      this.message.success('已加入购物车');
    }).catch(() => {
      this.message.error('加入购物车失败');
    });
    if (environment.production) {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const customer = JSON.parse(localStorage.getItem('customer') || '{}');
      (window as any).dataLayer.push({
        event: 'dp_add_to_cart_click',
        from_page:
          (localStorage.getItem('token') ? 'afterLogin ' : 'beforeLogin ') +
          window.location.pathname,
        event_type: 'promotion',
        event_name: '',
        portal_name: this.isDealerPortal
          ? 'DealerPortal ' + customer?.customerCode
          : 'SalesPortal ' + userData?.queryUserPageInfoResDTO?.ntAccount,
        event_content: (this.isDealerPortal
          ? customer?.customerCode
          : userData?.queryUserPageInfoResDTO?.ntAccount) +
          ' ' + (this.joinedActivityId ?? ''),
      });
    }
  }

  private buildImageUrl(imageUrl: string): string {
    if (!imageUrl) { return 'assets/official-website/no-image.png'; }
    if (imageUrl.startsWith('http')) { return imageUrl; }
    return `https://spiderbeast2.blob.core.chinacloudapi.cn/pim-attachment/${imageUrl}`;
  }

  private formatTimeRange(start: string, end: string): string {
    const fmt = (s: string) => s ? moment(s).format('YYYY.MM.DD') : '';
    return `${fmt(start)} - ${fmt(end)}`;
  }

  formatTimeRangePublic(start: string, end: string): string {
    return this.formatTimeRange(start, end);
  }

  /**
   * 活动奖券图片：图片 ID（awardImgUrl）走 userCenterAdmin 文件下载接口换取图片，
   * 与 new-feature-guide / banner 同一取图方式；无 ID 时返回空串，由模板回退图标占位。
   */
  getAwardImageUrl(activity: PromotionActivitySummary): string {
    const fileId = activity?.awardImgUrl || activity?.activityMechanism?.award?.imgUrl;
    return fileId
      ? `${environment.userCenterAdminUrl}/file/download/${fileId}`
      : '';
  }

  /** 图片加载失败（文件 ID 失效或文件服务异常）时标记该活动，模板切换为图标占位 */
  onAwardImageError(activity: PromotionActivitySummary): void {
    this.awardImgBroken.add(activity.activityId);
  }

  calcProgressRate(activity: PromotionActivitySummary): number {
    const target = activity.progressTarget ?? 0;
    const current = activity.progressCurrent ?? 0;
    return target ? Math.round((current / target) * 100) : 0;
  }

  calcRemainingAmount(activity: PromotionActivitySummary): number {
    const remaining = activity.progressRemaining ?? 0;
    const target = activity.progressTarget ?? 0;
    const current = activity.progressCurrent ?? 0;
    return remaining > 0 ? remaining : Math.max(0, target - current);
  }

  /** 金额（CNY）显示千分位；数量（EA）原样返回 */
  formatDisplayValue(value: number, baselineType: string): string {
    const num = Number(value ?? 0);
    return baselineType === 'By Qty' ? String(num) : num.toLocaleString('en-US');
  }
}
